import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import {
  Database, ChevronRight, CheckCircle2, XCircle, RotateCcw, Filter,
  Shuffle, ArrowRight, ArrowLeft, BookMarked, Flame, TrendingUp, Award, Sparkles,
} from "lucide-react";

const TYPE_LABEL = {
  definition: { en: "Definitions", ar: "التعريفات" },
  true_false: { en: "True / False", ar: "صح وخطأ" },
  completion: { en: "Fill-in-blank", ar: "أكمل" },
  mcq:        { en: "Multiple Choice", ar: "اختيار متعدد" },
  short_answer: { en: "Short Answer", ar: "إجابة قصيرة" },
  list:       { en: "List", ar: "عدّد" },
};
const TYPE_COLOR = {
  definition: "#4A675C",
  true_false: "#D97757",
  completion: "#6E8D81",
  mcq: "#5a7a6e",
  short_answer: "#C26544",
  list: "#8a6ea3",
};

function normalizeType(t) {
  // Backend returns e.g. "definitions" → "definition" (singular); match our label keys.
  if (!t) return "short_answer";
  const s = t.endsWith("s") && !["false"].includes(t) ? t.slice(0, -1) : t;
  const key = s === "true_fal" ? "true_false" : s;
  return TYPE_LABEL[key] ? key : t;
}

export default function Questions() {
  const { user, loading } = useAuth();
  const { lang, isRTL } = useLanguage();
  const navigate = useNavigate();

  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    else if (!loading && user && user.status === "pending") navigate("/pending", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    // One-time fetch on mount. `api` and `setChapters` are stable references.
    let cancelled = false;
    api.get("/questions/chapters")
      .then((r) => { if (!cancelled) setChapters(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-bone">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-sage text-white flex items-center justify-center shrink-0">
            <Database className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-sage font-bold">
              {isRTL ? "بنك أسئلة" : "Questions Databank"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {isRTL ? "تدرّب على أسئلة حقيقية من المنهج" : "Practise real exam-style questions"}
            </h1>
            <p className="mt-2 text-muted-foreground max-w-2xl leading-relaxed">
              {isRTL
                ? "+500 سؤال مستخرج من بنك أسئلة الهيئة العامة للرعاية الصحية (فرع بورسعيد) — مقسّمة حسب الفصل والنوع."
                : `${chapters.reduce((s, c) => s + c.total, 0)}+ questions from the Egypt Healthcare Authority question bank — grouped by chapter and type.`}
            </p>
          </div>
        </div>

        {!selectedChapter ? (
          <ChapterPicker chapters={chapters} onSelect={setSelectedChapter} lang={lang} isRTL={isRTL} />
        ) : (
          <QuestionRunner
            chapter={selectedChapter}
            onBack={() => setSelectedChapter(null)}
            lang={lang}
            isRTL={isRTL}
          />
        )}
      </main>
    </div>
  );
}

/* ============ Chapter picker ============ */

const ChapterCard = React.memo(function ChapterCard({ ch, onSelect, lang, isRTL }) {
  // Memoise the non-zero type counts so the `entries().filter()` chain runs
  // once per card (not on every parent render).
  const nonZeroTypes = useMemo(
    () => Object.entries(ch.counts || {}).filter(([, n]) => n > 0),
    [ch.counts],
  );
  return (
    <button
      onClick={() => ch.total > 0 && onSelect(ch)}
      disabled={!ch.total}
      data-testid={`qdb-chapter-${ch.chapter_id}`}
      className="group text-start bg-white border border-border hover:border-sage/50 rounded-2xl p-5 disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-9 h-9 rounded-xl bg-sage/10 text-sage font-display font-bold flex items-center justify-center">
          {ch.number}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
          {ch.total} {isRTL ? "سؤال" : "q’s"}
        </div>
      </div>
      <div className="font-display text-lg font-semibold leading-tight mb-3">{ch.title}</div>
      <div className="flex flex-wrap gap-1.5">
        {nonZeroTypes.map(([k, n]) => {
          const key = normalizeType(k);
          const color = TYPE_COLOR[key] || "#6E7A75";
          return (
            <span
              key={k}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
              style={{ background: `${color}15`, color }}
            >
              {TYPE_LABEL[key]?.[lang] || k} · {n}
            </span>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-end text-xs text-sage font-medium group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">
        {isRTL ? "ابدأ التدريب" : "Start practice"} <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
});

function ChapterPicker({ chapters, onSelect, lang, isRTL }) {
  if (!chapters.length) {
    return (
      <div className="mt-10 p-10 text-center text-muted-foreground rounded-2xl bg-white border border-border">
        {isRTL ? "لم يتم تحميل الأسئلة بعد." : "Questions haven't been loaded yet. Ask your admin to import the PDF."}
      </div>
    );
  }
  return (
    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="qdb-chapter-picker">
      {chapters.map((ch) => (
        <ChapterCard key={ch.chapter_id} ch={ch} onSelect={onSelect} lang={lang} isRTL={isRTL} />
      ))}
    </div>
  );
}

/* ============ Question runner ============ */

function QuestionRunner({ chapter, onBack, lang, isRTL }) {
  const [allQs, setAllQs] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [shuffled, setShuffled] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // { q.id: { value, correct } }
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    api.get(`/questions/${chapter.chapter_id}`).then((r) => {
      const qs = (r.data.questions || []).map((q, i) => ({
        ...q,
        type: normalizeType(q.type),
        _key: q.id || `${chapter.chapter_id}-q${i}`,
      }));
      setAllQs(qs);
    });
  }, [chapter.chapter_id]);

  const visible = useMemo(() => {
    let list = allQs;
    if (typeFilter !== "all") list = list.filter((q) => q.type === typeFilter);
    if (shuffled) list = [...list].sort(() => Math.random() - 0.5);
    return list;
  }, [allQs, typeFilter, shuffled]);

  useEffect(() => { setIdx(0); }, [typeFilter, shuffled, chapter.chapter_id]);

  const current = visible[idx];
  const recorded = current && answers[current._key];

  const record = (value, correct) => {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current._key]: { value, correct } }));
    setStreak((s) => (correct ? s + 1 : 0));
    // Fire-and-forget
    api.post("/questions/attempt", {
      question_id: current._key,
      chapter_id: chapter.chapter_id,
      correct: !!correct,
    }).catch(() => {});
  };

  const totals = useMemo(() => {
    const attempted = Object.keys(answers).length;
    const correct = Object.values(answers).filter((a) => a.correct).length;
    return { attempted, correct, total: visible.length };
  }, [answers, visible.length]);

  const typeChips = [
    { k: "all", label: { en: "All", ar: "الكل" } },
    ...Object.keys(TYPE_LABEL).map((k) => ({ k, label: TYPE_LABEL[k] })),
  ];

  if (!allQs.length) {
    return <div className="mt-10 p-10 text-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="mt-6" data-testid="qdb-runner">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <button
          onClick={onBack}
          data-testid="qdb-back"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-sage px-3 py-1.5 rounded-lg hover:bg-white border border-transparent hover:border-border transition"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} /> {isRTL ? "الفصول" : "All chapters"}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShuffled((x) => !x)}
            data-testid="qdb-shuffle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              shuffled ? "bg-terracotta text-white" : "bg-white border border-border text-muted-foreground hover:bg-sage/5"
            }`}
          >
            <Shuffle className="w-3.5 h-3.5" /> {isRTL ? "عشوائي" : "Shuffle"}
          </button>
          <button
            onClick={() => { setAnswers({}); setStreak(0); setIdx(0); }}
            data-testid="qdb-reset"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-border text-muted-foreground hover:bg-secondary"
          >
            <RotateCcw className="w-3.5 h-3.5" /> {isRTL ? "إعادة ضبط" : "Reset"}
          </button>
        </div>
      </div>

      {/* Chapter title + stats */}
      <div className="bg-white border border-border rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              {isRTL ? `الفصل ${chapter.number}` : `Chapter ${chapter.number}`}
            </div>
            <div className="font-display text-xl font-semibold">{chapter.title}</div>
          </div>
          <div className="flex items-center gap-3">
            <StatPill icon={TrendingUp} label={isRTL ? "تقدّم" : "Progress"} value={`${totals.attempted}/${totals.total}`} />
            <StatPill icon={Award} label={isRTL ? "صحيحة" : "Correct"} value={totals.correct} color="#4A675C" />
            <StatPill icon={Flame} label={isRTL ? "سلسلة" : "Streak"} value={streak} color="#D97757" />
          </div>
        </div>

        {/* Filter chips */}
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="qdb-filters">
          {typeChips.map((c) => {
            const active = typeFilter === c.k;
            const n = c.k === "all" ? allQs.length : allQs.filter((q) => q.type === c.k).length;
            if (n === 0 && c.k !== "all") return null;
            return (
              <button
                key={c.k}
                onClick={() => setTypeFilter(c.k)}
                data-testid={`qdb-filter-${c.k}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
                  active ? "bg-sage text-white" : "bg-secondary text-muted-foreground hover:bg-sage/5"
                }`}
              >
                <Filter className="w-3 h-3" />
                {c.label[lang]} · {n}
              </button>
            );
          })}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-sage transition-all"
          style={{ width: `${((idx + 1) / Math.max(1, visible.length)) * 100}%` }}
        />
      </div>

      {/* Question card */}
      {current && (
        <QuestionCard
          q={current}
          idx={idx}
          total={visible.length}
          recorded={recorded}
          onAnswer={record}
          lang={lang}
          isRTL={isRTL}
        />
      )}

      {/* Nav */}
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          data-testid="qdb-prev"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-border hover:bg-secondary disabled:opacity-30 text-sm font-medium"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} /> {isRTL ? "السابق" : "Prev"}
        </button>
        <div className="text-xs text-muted-foreground">
          {idx + 1} / {visible.length}
        </div>
        <button
          onClick={() => setIdx((i) => Math.min(visible.length - 1, i + 1))}
          disabled={idx >= visible.length - 1}
          data-testid="qdb-next"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sage hover:bg-sage-dark text-white disabled:opacity-30 text-sm font-medium"
        >
          {isRTL ? "التالي" : "Next"} <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
        </button>
      </div>
    </div>
  );
}

/* ============ Question card (handles every type) ============ */

function QuestionCard({ q, idx, total, recorded, onAnswer, lang, isRTL }) {
  return (
    <div
      className="bg-white border border-border rounded-2xl p-6 sm:p-8"
      data-testid={`qdb-card-${q._key}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
          style={{ background: TYPE_COLOR[q.type] || "#4A675C" }}
        >
          {TYPE_LABEL[q.type]?.[lang] || q.type}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {isRTL ? `س ${idx + 1}` : `Q ${idx + 1}`} / {total}
        </span>
      </div>

      <div className="font-display text-lg sm:text-xl font-semibold leading-snug mb-5">{q.question}</div>

      {q.type === "mcq" && <McqBody q={q} recorded={recorded} onAnswer={onAnswer} />}
      {q.type === "true_false" && <TrueFalseBody q={q} recorded={recorded} onAnswer={onAnswer} lang={lang} />}
      {(q.type === "definition" || q.type === "completion" || q.type === "short_answer" || q.type === "list") && (
        <RevealBody q={q} recorded={recorded} onAnswer={onAnswer} lang={lang} isRTL={isRTL} />
      )}
    </div>
  );
}

function McqBody({ q, recorded, onAnswer }) {
  const opts = q.options || [];
  const correctIdx = typeof q.correct_index === "number" ? q.correct_index : 0;
  const selectedIdx = recorded?.value;
  return (
    <div className="space-y-2.5" data-testid="qdb-mcq">
      {opts.map((opt, i) => {
        const isSel = selectedIdx === i;
        const isCorrect = recorded && i === correctIdx;
        const isWrong = recorded && isSel && i !== correctIdx;
        return (
          <button
            key={`opt-${i}-${(opt || "").slice(0, 24)}`}
            disabled={!!recorded}
            onClick={() => onAnswer(i, i === correctIdx)}
            data-testid={`qdb-opt-${i}`}
            className={`w-full text-start p-4 rounded-xl border transition flex items-center gap-3 ${
              isCorrect ? "bg-sage/10 border-sage"
              : isWrong ? "bg-terracotta/10 border-terracotta"
              : isSel ? "bg-secondary border-sage"
              : "bg-white border-border hover:bg-secondary/50"
            }`}
          >
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 ${
              isWrong ? "bg-terracotta text-white"
              : (isCorrect || isSel) ? "bg-sage text-white"
              : "bg-secondary text-muted-foreground"
            }`}>
              {String.fromCharCode(65 + i)}
            </span>
            <span className="text-sm leading-relaxed flex-1">{opt}</span>
            {isCorrect && <CheckCircle2 className="w-4 h-4 text-sage" />}
            {isWrong && <XCircle className="w-4 h-4 text-terracotta" />}
          </button>
        );
      })}
    </div>
  );
}

function TrueFalseBody({ q, recorded, onAnswer, lang }) {
  const isAr = lang === "ar";
  const correctVal = String(q.answer || "").toLowerCase().startsWith("t") ? "true" : "false";
  const sel = recorded?.value;
  return (
    <div className="space-y-3" data-testid="qdb-tf">
      <div className="grid grid-cols-2 gap-3">
        {["true", "false"].map((v) => {
          const isSel = sel === v;
          const isCorrect = recorded && v === correctVal;
          const isWrong = recorded && isSel && v !== correctVal;
          return (
            <button
              key={v}
              disabled={!!recorded}
              onClick={() => onAnswer(v, v === correctVal)}
              data-testid={`qdb-tf-${v}`}
              className={`py-4 rounded-xl border font-semibold text-sm transition ${
                isCorrect ? "bg-sage text-white border-sage"
                : isWrong ? "bg-terracotta text-white border-terracotta"
                : isSel ? "bg-secondary border-sage"
                : "bg-white border-border hover:bg-secondary/50"
              }`}
            >
              {v === "true" ? (isAr ? "صحيح" : "True") : (isAr ? "خطأ" : "False")}
            </button>
          );
        })}
      </div>
      {recorded && q.correction && (
        <div className="rounded-xl bg-sage/5 border border-sage/30 p-4 text-sm leading-relaxed">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-1">{isAr ? "التصحيح" : "Correction"}</div>
          {q.correction}
        </div>
      )}
    </div>
  );
}

function RevealBody({ q, recorded, onAnswer, lang, isRTL }) {
  const isAr = lang === "ar";
  const revealed = !!recorded;
  return (
    <div className="space-y-3" data-testid="qdb-reveal">
      {!revealed && (
        <div className="rounded-xl bg-bone border border-border p-4 text-sm text-muted-foreground italic leading-relaxed">
          {isAr ? "فكّر في الإجابة أولاً…" : "Take a moment to think about your answer…"}
        </div>
      )}
      {revealed && (
        <div className="rounded-xl bg-sage/5 border border-sage/30 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-1.5">
            <Sparkles className="w-3 h-3 inline-block me-1" />
            {isAr ? "الإجابة" : "Model answer"}
          </div>
          <div className="text-sm leading-relaxed whitespace-pre-line">{q.answer}</div>
        </div>
      )}
      {!revealed ? (
        <button
          onClick={() => onAnswer("revealed", true)}
          data-testid="qdb-reveal-btn"
          className="w-full sm:w-auto inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-4 py-2.5 rounded-xl text-sm transition"
        >
          <BookMarked className="w-4 h-4" /> {isAr ? "اعرض الإجابة" : "Reveal answer"}
        </button>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => onAnswer("got-it", true)}
            data-testid="qdb-got-it"
            className="inline-flex items-center gap-1.5 bg-sage hover:bg-sage-dark text-white font-medium px-3 py-1.5 rounded-lg text-xs transition"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> {isAr ? "فهمت" : "Got it"}
          </button>
          <button
            onClick={() => onAnswer("need-review", false)}
            data-testid="qdb-need-review"
            className="inline-flex items-center gap-1.5 bg-white border border-terracotta/40 text-terracotta hover:bg-terracotta/5 font-medium px-3 py-1.5 rounded-lg text-xs transition"
          >
            <RotateCcw className="w-3.5 h-3.5" /> {isAr ? "أحتاج مراجعة" : "Need review"}
          </button>
        </div>
      )}
    </div>
  );
}

function StatPill({ icon: Icon, label, value, color = "#6E7A75" }) {
  return (
    <div className="inline-flex items-center gap-2 bg-bone border border-border rounded-xl px-3 py-2">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="leading-tight">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
        <div className="font-display text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}
